import { useState, useEffect } from 'react'
import * as Sentry from '@sentry/react'
import { useDepartment } from '../context/DepartmentContext'
import { useToast } from '../context/ToastContext'
import { useAuth } from '../context/AuthContext'
import { Settings, Package, Shield, Activity, Users, Globe, Star, ShoppingBag, Briefcase, FileText, ToggleRight, Tag, Store } from 'lucide-react'
import { ensureMenuSeeded } from '../lib/customerMenuService'
import { ADMIN_EMAILS } from '../config/adminConfig'

import AuditTab from '../components/admin/AuditTab'
import DepartmentsTab from '../components/admin/DepartmentsTab'
import ProductsTab from '../components/admin/ProductsTab'
import PriceBookTab from '../components/admin/PriceBookTab'
import DistributionPricesTab from '../components/admin/DistributionPricesTab'
import CategoriesTab from '../components/admin/CategoriesTab'
import PublicSiteTab from '../components/admin/PublicSiteTab'
import ReviewsTab from '../components/admin/ReviewsTab'
import OrdersTab from '../components/admin/OrdersTab'
import OrderSettingsTab from '../components/admin/OrderSettingsTab'
import InvoicesTab from '../components/admin/InvoicesTab'
import CareersTab from '../components/admin/CareersTab'
import UsersTab from '../components/admin/UsersTab'

const BASE_TABS = [
  { id: 'orders', label: 'Orders', icon: ShoppingBag },
  { id: 'invoices', label: 'Invoices', icon: FileText },
  { id: 'order-settings', label: 'Order Settings', icon: ToggleRight },
  { id: 'reviews', label: 'Reviews', icon: Star },
  { id: 'careers', label: 'Careers', icon: Briefcase },
  { id: 'products', label: 'Products', icon: Package },
  { id: 'pricebook', label: 'Price Book', icon: Tag },
  { id: 'distprices', label: 'Distribution Prices', icon: Store },
  { id: 'categories', label: 'Categories', icon: Settings },
  { id: 'departments', label: 'Departments', icon: Shield },
  { id: 'publicsite', label: 'Public Site', icon: Globe },
  { id: 'audit', label: 'Audit Log', icon: Activity },
]

export default function Admin() {
  const { currentDepartmentId, departments, activateDepartment } = useDepartment()
  const { addToast } = useToast()
  const { user, userProfile } = useAuth()
  const [tab, setTab] = useState('orders')
  const [invoiceFromOrder, setInvoiceFromOrder] = useState(null)

  const isAdmin = userProfile?.role === 'admin' || ADMIN_EMAILS.includes(user?.email)

  useEffect(() => {
    if (isAdmin) ensureMenuSeeded().catch(err => {
      console.warn('[Admin] ensureMenuSeeded failed:', err?.message)
      Sentry.captureException(err, { extra: { fn: 'Admin.ensureMenuSeeded' } })
    })
  }, [isAdmin])

  // Insert the admin-only Users tab after Departments (index 8 in BASE_TABS).
  const DEPT_INDEX = BASE_TABS.findIndex(t => t.id === 'departments')
  const TABS = isAdmin
    ? [
        ...BASE_TABS.slice(0, DEPT_INDEX + 1),
        { id: 'users', label: 'Users', icon: Users },
        ...BASE_TABS.slice(DEPT_INDEX + 1),
      ]
    : BASE_TABS

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-bold">Product & Configuration Management</h1>

      <div className="flex gap-1 overflow-x-auto">
        {TABS.map(t => {
          const Icon = t.icon
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm whitespace-nowrap ${
                tab === t.id ? 'bg-brand-700 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'
              }`}
            >
              <Icon size={14} />
              {t.label}
            </button>
          )
        })}
      </div>

      {tab === 'orders' && <OrdersTab addToast={addToast} isAdmin={isAdmin} department={currentDepartmentId} onInvoice={(order) => { setTab('invoices'); setInvoiceFromOrder(order) }} />}
      {tab === 'invoices' && <InvoicesTab addToast={addToast} department={currentDepartmentId} prefillOrder={invoiceFromOrder} clearPrefill={() => setInvoiceFromOrder(null)} />}
      {tab === 'order-settings' && <OrderSettingsTab addToast={addToast} department={currentDepartmentId} />}
      {tab === 'reviews' && <ReviewsTab addToast={addToast} />}
      {tab === 'careers' && <CareersTab addToast={addToast} department={currentDepartmentId} />}
      {tab === 'products' && <ProductsTab department={currentDepartmentId} />}
      {tab === 'pricebook' && <PriceBookTab />}
      {tab === 'distprices' && <DistributionPricesTab />}
      {tab === 'categories' && <CategoriesTab department={currentDepartmentId} />}
      {tab === 'departments' && <DepartmentsTab departments={departments} onActivate={activateDepartment} addToast={addToast} />}
      {tab === 'publicsite' && <PublicSiteTab addToast={addToast} department={currentDepartmentId} />}
      {tab === 'audit' && <AuditTab department={currentDepartmentId} />}
      {tab === 'users' && isAdmin && <UsersTab />}
    </div>
  )
}
